<template>
    <VcModal
        :visible="visible"
        width="500px"
        :header="t('common.reorder')"
        :dismissable-mask="false"
        close-on-escape
        @close="handleCancel"
    >
        <div class="queue-dashboard-order-form">
            <draggable
                :list="items"
                @end="onReorder"
            >
                <div
                    v-for="element in items"
                    :key="element.key"
                    class="order-item"
                >
                    <redesign-base-icon :name="'GripVertical'" />
                    <span class="label">
                        {{ element.label }}
                    </span>
                </div>
            </draggable>
        </div>
        <template #footer>
            <div class="flex justify-end gap-2">
                <VcButton
                    type="outline"
                    color="secondary"
                    @click="handleResetToDefault"
                >
                    <span>{{ t('common.resetToDefault') }}</span>
                </VcButton>
                <VcButton
                    type="outline"
                    color="secondary"
                    @click="handleCancel"
                >
                    <span>{{ t('common.cancel') }}</span>
                </VcButton>
                <VcButton
                    color="primary"
                    @click="handleSave"
                >
                    <span>{{ t('common.save') }}</span>
                </VcButton>
            </div>
        </template>
    </VcModal>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { VueDraggableNext as draggable } from 'vue-draggable-next'
import type { QueueDashboardConfig } from '@/types/dashboard'
import { getAllMetrics, buildDefaultQueueDashboardConfig } from '@/enum/redesignQueueDashboardStatistics'
import { VcModal, VcButton } from '@voicenter-team/voicenter-ui-plus'
import { useI18n } from 'vue-i18n'
import { useConfirmBeforeClose } from '@/composables/useConfirmBeforeClose'
import RedesignBaseIcon from '@/components/RedesignBaseComponents/RedesignBaseIcon.vue'

const props = defineProps<{
    modelValue: QueueDashboardConfig
    visible: boolean
    activeKeys?: string[]
}>()

const emit = defineEmits<{
    (e: 'update:modelValue', value: QueueDashboardConfig): void
    (e: 'close'): void
}>()

type OrderItem = {
    key: string
    label: string
    order: number
}

const { t } = useI18n()
const { closeWithConfirm } = useConfirmBeforeClose()
const items = ref<OrderItem[]>([])
const originalConfig = ref<QueueDashboardConfig>({})
const isSaving = ref(false)

watch(
    () => props.visible,
    (isVisible) => {
        if (isVisible) {
            originalConfig.value = JSON.parse(JSON.stringify(props.modelValue))
            isSaving.value = false
        }
    }
)

watch(
    [ () => props.modelValue, () => props.activeKeys ],
    ([ config ]) => {
        const metricsWithLabels = getAllMetrics()
        const activeSet = new Set(props.activeKeys ?? [])

        items.value = Object.keys(config)
            .filter((key) => activeSet.size === 0 || activeSet.has(key))
            .map((key) => ({
                key,
                label: config?.[key]?.customLabel || metricsWithLabels[key]?.label || key,
                order: config[key].order ?? metricsWithLabels[key]?.order ?? 0
            }))
            .sort((a, b) => a.order - b.order)
    },
    {
        immediate: true,
        deep: true
    }
)

function applyOrderToConfig () {
    const updated = { ...props.modelValue }
    items.value.forEach((item, index) => {
        updated[item.key] = {
            ...updated[item.key],
            order: index
        }
    })
    emit('update:modelValue', updated)
}

/**
 * Buttons handlers
 */
function forceCloseModal () {
    if (!isSaving.value) {
        emit('update:modelValue', originalConfig.value)
    }
    isSaving.value = false
    emit('close')
}

function handleCancel () {
    closeWithConfirm(forceCloseModal)
}

function handleSave () {
    isSaving.value = true
    applyOrderToConfig()
    emit('close')
}

function onReorder () {
    applyOrderToConfig()
}

function handleResetToDefault () {
    const defaultConfig = buildDefaultQueueDashboardConfig()
    const updated = { ...props.modelValue }

    for (const key in updated) {
        updated[key] = {
            ...updated[key],
            order: defaultConfig[key]?.order ?? updated[key].order
        }
    }

    emit('update:modelValue', updated)
}
</script>

<style scoped lang="scss">
.queue-dashboard-order-form {
    @apply flex flex-col gap-2;
}

.order-item {
    @apply flex cursor-grab items-center gap-3 px-3 py-2 rounded border;
    border-color: var(--colorWidgetBorder-color);
    margin-bottom: 5px;
}

.drag-handle {
  @apply cursor-grab text-gray-400 select-none;
  font-size: 18px;
}

</style>
